import * as React from 'react'
import {cva, type VariantProps} from 'class-variance-authority'
import {
  Dialog as AriakitDialog,
  DialogDismiss as AriakitDialogDismiss,
  DialogHeading as AriakitDialogHeading,
  type DialogProps as AriakitDialogProps
} from '@ariakit/react'

const dialogVariants = cva(
  'fixed inset-0 z-[1050] m-auto h-fit rounded-[8px] bg-background shadow-[0_4px_24px_rgba(0,0,0,0.15)]',
  {
    variants: {
      size: {
        default: 'w-[660px]',
        sm: 'w-[400px]',
        // 3rem is 42px under the console's 14px root font size
        lg: 'w-[940px] max-w-[calc(100vw-3rem)]'
      }
    },
    defaultVariants: {
      size: 'default'
    }
  }
)

type DialogProps = Pick<AriakitDialogProps, 'open' | 'onClose' | 'hideOnEscape' | 'children'> &
  VariantProps<typeof dialogVariants>

type SectionProps = {children: React.ReactNode}

export function Dialog({
  open,
  onClose,
  hideOnEscape,
  size,
  children
}: DialogProps): React.ReactElement {
  return (
    <AriakitDialog
      open={open}
      onClose={onClose}
      hideOnEscape={hideOnEscape}
      unmountOnHide
      // #43809: with Ariakit's default, the click that opens a dialog counted as an outside click and closed it at once
      hideOnInteractOutside={false}
      backdrop={<div className="fixed inset-0 z-[1040] bg-black/50" />}
      className={dialogVariants({size})}
    >
      {children}
    </AriakitDialog>
  )
}

type DialogHeadingProps = SectionProps & {
  // false omits the visible Close button, for a dialog the user must not close
  dismissible?: boolean
}

export function DialogHeading({
  children,
  dismissible = true
}: DialogHeadingProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-border pt-[16px] px-[16px] pb-[12px]">
      <AriakitDialogHeading className="m-0 p-0 text-[24px] text-body">
        {children}
      </AriakitDialogHeading>
      {dismissible && (
        <AriakitDialogDismiss
          aria-label="Close"
          className="cursor-pointer appearance-none border-0 bg-transparent p-0 text-[24px] leading-none text-body"
        >
          <span aria-hidden="true">&times;</span>
        </AriakitDialogDismiss>
      )}
    </div>
  )
}

export function DialogBody({children}: SectionProps): React.ReactElement {
  return <div className="max-h-[70vh] overflow-y-auto p-[16px]">{children}</div>
}

export function DialogFooter({children}: SectionProps): React.ReactElement {
  return <div className="flex justify-end gap-[8px] p-[16px]">{children}</div>
}
